import { useCallback, useLayoutEffect, useRef, useState } from 'react'

interface ICarouselNav {
  overflowing: boolean
  atStart: boolean
  atEnd: boolean
}

// Shared by the grid's recommendation band and the cart's "Forgetting Something?" row: pills exist
// only while the track overflows, and each end disables its own pill. `slideCount` re-measures when
// the row's membership changes, since a shorter row may stop overflowing.
const useCarouselNav = (slideCount: number) => {
  const trackRef = useRef<HTMLDivElement>(null)
  const [nav, setNav] = useState<ICarouselNav>({ overflowing: false, atStart: true, atEnd: false })

  const measureNav = useCallback(() => {
    const track = trackRef.current
    if (!track) return
    const { scrollLeft, scrollWidth, clientWidth } = track
    const next = {
      overflowing: scrollWidth > clientWidth + 1,
      atStart: scrollLeft <= 0,
      atEnd: scrollLeft + clientWidth >= scrollWidth - 1
    }
    setNav(prev =>
      prev.overflowing === next.overflowing && prev.atStart === next.atStart && prev.atEnd === next.atEnd ? prev : next
    )
  }, [])

  useLayoutEffect(() => {
    const track = trackRef.current
    if (!track) return

    measureNav()

    const observer = new ResizeObserver(measureNav)
    observer.observe(track)
    return () => observer.disconnect()
  }, [slideCount, measureNav])

  const slide = (direction: 1 | -1) => {
    if (!trackRef.current) return
    trackRef.current.scrollBy({ left: (direction * trackRef.current.clientWidth) / 2, behavior: 'smooth' })
  }

  return { trackRef, nav, measureNav, slide }
}

export default useCarouselNav
